import { useEffect, useRef, useState } from 'react';
import {
  User,
  FileText,
  BookOpen,
  Bold,
  Italic,
  Link,
  List,
  Terminal,
  Plus,
  MoreVertical,
  X,
  Pencil,
  Loader2,
} from 'lucide-react';

import { useProfile, useUpdateProfile, useUploadAvatar, useSetAvatarUrl } from '@/hooks/useProfile';
import { getErrorMessage } from '@/utils/error';
import { resolveImageUrl } from '@/utils/url';
import type { TechCategoryDTO } from '@/types/profile';

export function ProfileEditorPage() {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const setAvatarUrl = useSetAvatarUrl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [biography, setBiography] = useState('');
  const [techCategories, setTechCategories] = useState<TechCategoryDTO[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUrlInput, setAvatarUrlInput] = useState('');
  const [showAvatarUrlInput, setShowAvatarUrlInput] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setRole(profile.role || '');
      setExecutiveSummary(profile.executiveSummary || '');
      setBiography(profile.biography || '');
      setTechCategories(profile.techCategories || []);
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        name,
        role,
        executiveSummary,
        biography,
        techCategories,
      });
      setSaveMessage('Profile saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage(getErrorMessage(err));
    }
  };

  const handleDiscard = () => {
    if (profile) {
      setName(profile.name || '');
      setRole(profile.role || '');
      setExecutiveSummary(profile.executiveSummary || '');
      setBiography(profile.biography || '');
      setTechCategories(profile.techCategories || []);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    try {
      await uploadAvatar.mutateAsync(file);
    } catch (err) {
      setAvatarPreview(null);
      setSaveMessage(getErrorMessage(err));
    }
  };

  const handleAvatarUrlSubmit = async () => {
    const url = avatarUrlInput.trim();
    if (!url) return;
    setAvatarPreview(url);
    try {
      await setAvatarUrl.mutateAsync(url);
      setAvatarUrlInput('');
      setShowAvatarUrlInput(false);
    } catch (err) {
      setAvatarPreview(null);
      setSaveMessage(getErrorMessage(err));
    }
  };

  const addTag = (categoryIndex: number, tag: string) => {
    if (!tag.trim()) return;
    setTechCategories((prev) =>
      prev.map((cat, i) =>
        i === categoryIndex ? { ...cat, tags: [...cat.tags, tag.trim()] } : cat,
      ),
    );
  };

  const removeTag = (categoryIndex: number, tagIndex: number) => {
    setTechCategories((prev) =>
      prev.map((cat, i) =>
        i === categoryIndex
          ? { ...cat, tags: cat.tags.filter((_, ti) => ti !== tagIndex) }
          : cat,
      ),
    );
  };

  const addCategory = () => {
    setTechCategories((prev) => [...prev, { id: null, title: 'New Category', tags: [] }]);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-dark-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-error">Failed to load profile: {getErrorMessage(error)}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-12 p-12">
      {/* Sticky Header Actions */}
      <div className="flex items-end justify-between border-b border-white/5 pb-8">
        <div>
          <h2 className="font-headline text-4xl font-extrabold tracking-tight text-white">
            Profile Architecture
          </h2>
          <p className="mt-2 max-w-md text-dark-on-surface-variant">
            Refine your digital identity and technical footprint within the precision
            ecosystem.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {saveMessage && (
            <span className="text-sm text-dark-secondary">{saveMessage}</span>
          )}
          <button
            onClick={handleDiscard}
            className="rounded-full border border-dark-outline-variant px-6 py-2 text-sm font-semibold text-dark-on-surface transition-all hover:bg-white/5"
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={updateProfile.isPending}
            className="rounded-full bg-gradient-to-r from-dark-primary-container to-dark-secondary-container px-8 py-2 text-sm font-bold text-white shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
          >
            {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-12 gap-8">
        {/* Identity Card */}
        <div className="group relative col-span-12 overflow-hidden rounded-xl bg-dark-surface-container-low p-8 shadow-[0_40px_40px_-10px_rgba(0,26,64,0.4)] outline outline-1 outline-dark-outline-variant/15 lg:col-span-4">
          <div className="absolute right-0 top-0 p-4 opacity-10 transition-opacity group-hover:opacity-20">
            <User className="h-16 w-16" />
          </div>
          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Avatar */}
            <div className="relative mb-6">
              <div className="h-32 w-32 overflow-hidden rounded-xl border-2 border-dark-primary/20 bg-dark-surface-container p-1">
                {avatarPreview || profile?.avatarUrl ? (
                  <img
                    src={avatarPreview || resolveImageUrl(profile?.avatarUrl) || ''}
                    alt="Avatar"
                    className="h-full w-full rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-lg bg-dark-surface-container-highest text-3xl font-bold text-dark-on-surface">
                    {name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAvatar.isPending || setAvatarUrl.isPending}
                className="absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full border-4 border-dark-surface-container-low bg-dark-secondary shadow-xl transition-transform hover:scale-110"
              >
                {uploadAvatar.isPending || setAvatarUrl.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-dark-on-secondary-container" />
                ) : (
                  <Pencil className="h-4 w-4 text-dark-on-secondary-container" />
                )}
              </button>
            </div>
            {/* Avatar URL Input Toggle */}
            <button
              onClick={() => setShowAvatarUrlInput((prev) => !prev)}
              className="mt-2 text-[10px] font-bold uppercase tracking-widest text-dark-secondary transition-colors hover:text-dark-primary"
            >
              {showAvatarUrlInput ? 'Cancel' : 'Or paste image URL'}
            </button>
            {showAvatarUrlInput && (
              <div className="mt-3 flex w-full gap-2">
                <input
                  type="url"
                  value={avatarUrlInput}
                  onChange={(e) => setAvatarUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAvatarUrlSubmit()}
                  placeholder="https://example.com/avatar.png"
                  className="flex-1 rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
                <button
                  onClick={handleAvatarUrlSubmit}
                  disabled={!avatarUrlInput.trim() || setAvatarUrl.isPending}
                  className="rounded-lg bg-dark-secondary-container px-3 py-2 text-[10px] font-bold text-white transition-all hover:opacity-80 disabled:opacity-50"
                >
                  {setAvatarUrl.isPending ? '...' : 'Set'}
                </button>
              </div>
            )}
            {/* Name & Role */}
            <div className="w-full space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-dark-secondary">
                  Identity
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border-none bg-dark-surface-container-lowest px-4 py-2 font-headline text-xl font-bold text-white transition-all focus:ring-1 focus:ring-dark-primary"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-dark-secondary">
                  System Role
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full rounded-lg border-none bg-dark-surface-container-lowest px-4 py-2 text-sm text-dark-on-surface-variant transition-all focus:ring-1 focus:ring-dark-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bio / Narrative Section */}
        <div className="col-span-12 space-y-8 lg:col-span-8">
          {/* Executive Summary */}
          <div className="rounded-xl bg-dark-surface-container-low p-8 outline outline-1 outline-dark-outline-variant/15">
            <div className="mb-6 flex items-center gap-3">
              <FileText className="h-5 w-5 text-dark-primary" />
              <h3 className="font-headline text-lg font-bold text-white">Executive Summary</h3>
            </div>
            <textarea
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
              placeholder="Enter a concise hook about your expertise..."
              className="h-24 w-full resize-none rounded-lg border-none bg-dark-surface-container-lowest p-4 text-sm text-dark-on-surface transition-all focus:ring-1 focus:ring-dark-primary"
            />
          </div>

          {/* Professional Biography */}
          <div className="rounded-xl bg-dark-surface-container-low p-8 outline outline-1 outline-dark-outline-variant/15">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-dark-primary" />
                <h3 className="font-headline text-lg font-bold text-white">
                  Professional Biography
                </h3>
              </div>
              <div className="flex gap-1 rounded-md bg-dark-surface-container-highest p-1">
                <button className="rounded p-1.5 hover:bg-white/10">
                  <Bold className="h-4 w-4" />
                </button>
                <button className="rounded p-1.5 hover:bg-white/10">
                  <Italic className="h-4 w-4" />
                </button>
                <button className="rounded p-1.5 hover:bg-white/10">
                  <Link className="h-4 w-4" />
                </button>
                <button className="rounded p-1.5 hover:bg-white/10">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
            <textarea
              value={biography}
              onChange={(e) => setBiography(e.target.value)}
              placeholder="Tell your professional story..."
              className="h-48 w-full resize-y rounded-lg border-none bg-dark-surface-container-lowest p-4 text-sm text-dark-on-surface transition-all focus:ring-1 focus:ring-dark-primary"
            />
          </div>
        </div>
      </div>

      {/* Tech Stack Manager */}
      <div className="rounded-xl bg-dark-surface-container-low p-10 outline outline-1 outline-dark-outline-variant/15">
        <div className="mb-10 flex items-start justify-between">
          <div>
            <h3 className="flex items-center gap-3 font-headline text-2xl font-bold text-white">
              <Terminal className="h-6 w-6 text-dark-primary" />
              Technology Stack
            </h3>
            <p className="mt-1 text-sm text-dark-on-surface-variant">
              Categorize and manage your technical proficiencies.
            </p>
          </div>
          <button
            onClick={addCategory}
            className="flex items-center gap-2 rounded-lg bg-dark-surface-container-highest px-5 py-2 text-xs font-bold uppercase tracking-widest text-dark-secondary transition-all hover:bg-dark-surface-bright"
          >
            <Plus className="h-4 w-4" /> Add Category
          </button>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {techCategories.map((category, catIndex) => (
            <TechCategory
              key={catIndex}
              title={category.title}
              tags={category.tags}
              onTitleChange={(title) =>
                setTechCategories((prev) =>
                  prev.map((c, i) => (i === catIndex ? { ...c, title } : c)),
                )
              }
              onAddTag={(tag) => addTag(catIndex, tag)}
              onRemoveTag={(tagIndex) => removeTag(catIndex, tagIndex)}
            />
          ))}
        </div>
      </div>

      {/* Profile Status Indicator */}
      <div className="flex justify-end pt-12">
        <div className="flex items-center gap-4 rounded-full border border-white/5 bg-dark-surface-container-highest/50 px-6 py-3 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-dark-on-surface-variant">
              Profile Complete
            </span>
          </div>
          <div className="h-4 w-px bg-white/10" />
          <span className="text-[10px] font-bold text-slate-500">
            LAST SYNCED: {profile ? 'JUST NOW' : '---'}
          </span>
        </div>
      </div>
    </div>
  );
}

function TechCategory({
  title,
  tags,
  onTitleChange,
  onAddTag,
  onRemoveTag,
}: {
  title: string;
  tags: string[];
  onTitleChange: (title: string) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (index: number) => void;
}) {
  const [newTag, setNewTag] = useState('');

  const handleAddTag = () => {
    if (newTag.trim()) {
      onAddTag(newTag);
      setNewTag('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="bg-transparent text-[10px] font-bold uppercase tracking-[0.2em] text-dark-secondary focus:outline-none focus:ring-0"
        />
        <button className="text-slate-500 hover:text-white">
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-[200px] space-y-4 rounded-xl bg-dark-surface-container-lowest p-4">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="flex items-center gap-2 rounded-md border border-white/5 bg-dark-surface-container-highest px-3 py-1.5 text-xs font-medium text-dark-secondary"
            >
              {tag}
              <button onClick={() => onRemoveTag(tagIndex)} className="hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
            placeholder="New tag..."
            className="flex-1 rounded-lg border border-dashed border-dark-outline-variant bg-transparent px-3 py-2 text-xs text-dark-on-surface placeholder:text-slate-500 focus:border-dark-primary focus:outline-none"
          />
          <button
            onClick={handleAddTag}
            className="rounded-lg border border-dashed border-dark-outline-variant px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-all hover:border-dark-primary hover:text-dark-primary"
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
}
